import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { T, Icon, stitchSectionLabel, stitchTooltipSlotProps } from '../parts/stitch';
import { bestandsIcoon } from '../../ProjectFlow/FaseTemplates';
import { FLOW_PHASES } from '../../../config/projectFlow';
import { useProjectData } from '../ProjectDataContext';

const FASE_LABELS = {
  opstartfase: 'Opstart & briefing', offertefase: 'Offerte', concept: 'Concept',
  preproductie: 'Preproductie', productie: 'Productie', postproductie: 'Postproductie',
  afronding: 'Afronding', evaluatie: 'Evaluatie',
};

/**
 * Het documentmapje van het project: álle geüploade documenten, gegroepeerd
 * per fase. Uploaden gebeurt op de fase-kaart zelf; hier vind je alles terug.
 */
function DocumentsPanel() {
  const { id, projectNumber } = useProjectData();
  const [documenten, setDocumenten] = useState(null);
  const [openFases, setOpenFases] = useState(null); // Set van open fases (null = auto: eerste met docs)
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const laad = useCallback(async () => {
    if (!id) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${id}/documents`);
      setDocumenten(r.ok ? (await r.json())?.documenten || [] : []);
    } catch { setDocumenten([]); }
  }, [id]);
  useEffect(() => { laad(); }, [laad]);

  // Uploaden kan hier óók (was: alleen op de fase-kaart) — slepen of klikken,
  // meerdere bestanden tegelijk; de fase kies je in het dropdownnetje.
  // Guard tegen doorlopen na projectwissel/unmount: het id waarvoor de batch
  // startte moet nog steeds het actieve id zijn, anders stoppen we stil.
  const actueelIdRef = useRef(id);
  useEffect(() => {
    actueelIdRef.current = id;
    return () => { actueelIdRef.current = null; }; // unmount: batch stoppen
  }, [id]);

  const MAX_BATCH = 20;
  const MAX_BESTAND = 150 * 1024 * 1024; // spiegelt de multer-limiet

  const uploadBestanden = async (fileList) => {
    let files = Array.from(fileList || []).filter(Boolean);
    if (!files.length || uploading || !id) return;
    if (files.length > MAX_BATCH) {
      toast(`Maximaal ${MAX_BATCH} bestanden per keer — de eerste ${MAX_BATCH} worden geüpload.`, { icon: 'ℹ️' });
      files = files.slice(0, MAX_BATCH);
    }
    const batchId = id;
    setUploading(true);
    let gelukt = 0;
    for (const file of files) {
      if (actueelIdRef.current !== batchId) { setUploading(false); return; } // ander project/unmount — stoppen
      if (file.size > MAX_BESTAND) { toast.error(`${file.name}: groter dan 150 MB`); continue; }
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('fase', 'auto'); // AI/keywords kiezen de fase op bestandsnaam
        fd.append('naam', file.name.replace(/\.[^.]+$/, ''));
        if (projectNumber) fd.append('projectNumber', projectNumber);
        const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${batchId}/documents`, { method: 'POST', body: fd });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
        gelukt += 1;
        if (d.fase) toast(`${file.name} → ${FASE_LABELS[d.fase] || d.fase}`, { icon: '📁' });
      } catch (e) { toast.error(`${file.name}: ${e.message}`); }
    }
    if (actueelIdRef.current !== batchId) { setUploading(false); return; }
    if (gelukt) toast.success(gelukt === 1 ? 'Document geüpload' : `${gelukt} documenten geüpload`);
    setUploading(false);
    await laad();
  };

  const uploadZone = (
    <Box>
      <input ref={fileInputRef} type="file" multiple hidden
        onChange={(e) => { uploadBestanden(e.target.files); e.target.value = ''; }} />
      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'block', mb: 1 }}>
        De fase wordt automatisch bepaald op de bestandsnaam (callsheet → productie, draaiboek → preproductie, …) — je ziet per bestand waar hij belandt.
      </Typography>
      <Box
        onClick={() => !uploading && fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadBestanden(e.dataTransfer.files); }}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
          p: 2.5, borderRadius: '0.75rem', cursor: uploading ? 'default' : 'pointer',
          border: `2px dashed ${dragOver ? T.primary : T.outlineVariant}60`,
          bgcolor: dragOver ? `${T.primary}0d` : T.surfaceLow,
          transition: 'all 0.15s', '&:hover': { borderColor: `${T.primary}80` },
        }}>
        {uploading ? <CircularProgress size={18} /> : <Icon name="upload_file" size={20} sx={{ color: T.outline }} />}
        <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outline, fontWeight: 600 }}>
          {uploading ? 'Uploaden…' : 'Sleep documenten hierheen of klik om te kiezen'}
        </Typography>
      </Box>
    </Box>
  );

  const download = async (doc) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${id}/documents/${doc.id}/download`);
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      // Nieuw tabblad i.p.v. downloaden — PDF bekijk je meteen
      window.open(d.url, '_blank', 'noopener');
    } catch (e) { toast.error(`Download mislukt: ${e.message}`); }
  };

  if (documenten === null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 6, justifyContent: 'center' }}>
        <CircularProgress size={20} />
        <Typography sx={{ fontFamily: T.fontBody, color: T.outline }}>Documenten ophalen…</Typography>
      </Box>
    );
  }
  if (documenten.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {uploadZone}
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Icon name="folder_open" size={48} sx={{ color: T.outlineVariant, display: 'block', mx: 'auto', mb: 1 }} />
          <Typography sx={{ fontFamily: T.fontBody, color: T.outline, fontWeight: 500 }}>
            Nog geen documenten bij dit project
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outlineVariant, maxWidth: 420, mx: 'auto', mt: 0.5 }}>
            Sleep ze hierboven naar binnen, of upload op de fase-kaart in de flow.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Groeperen op fase, in de vaste fase-volgorde.
  const volgorde = Object.keys(FLOW_PHASES);
  const perFase = {};
  documenten.forEach((d) => { (perFase[d.fase] = perFase[d.fase] || []).push(d); });

  const fasesMetDocs = volgorde.filter((f) => perFase[f]);
  const isOpen = (f) => (openFases ? openFases.has(f) : f === fasesMetDocs[0]);
  const toggleFase = (f) => setOpenFases((prev) => {
    const basis = prev ? new Set(prev) : new Set(fasesMetDocs.filter(isOpen));
    if (basis.has(f)) basis.delete(f); else basis.add(f);
    return basis;
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {uploadZone}
      {fasesMetDocs.map((f) => (
        <Box key={f}>
          <Box onClick={() => toggleFase(f)} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', userSelect: 'none', py: 0.5 }}>
            <Icon name={isOpen(f) ? 'expand_more' : 'chevron_right'} size={18} sx={{ color: T.outline }} />
            <Typography sx={{ ...stitchSectionLabel }}>{FASE_LABELS[f] || f}</Typography>
            <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant, fontWeight: 700 }}>({perFase[f].length})</Typography>
          </Box>
          {isOpen(f) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {perFase[f].map((doc) => (
              <Box key={doc.id} onClick={() => download(doc)} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, cursor: 'pointer',
                borderRadius: '0.6rem', border: `1px solid ${T.surfaceHigh}`,
                '&:hover': { bgcolor: T.surfaceLow },
              }}>
                <Icon name={bestandsIcoon(doc.bestandsnaam)} size={20} sx={{ color: T.tertiary }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 600, color: T.text }}>
                    {doc.naam}
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
                    {doc.bestandsnaam}
                    {doc.uploaded_by ? ` · ${doc.uploaded_by}` : ''}
                    {doc.created_at ? ` · ${doc.created_at.slice(0, 10)}` : ''}
                  </Typography>
                </Box>
                <Tooltip title="Downloaden" slotProps={stitchTooltipSlotProps}>
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <Icon name="download" size={17} sx={{ color: T.outlineVariant }} />
                  </Box>
                </Tooltip>
              </Box>
            ))}
          </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

export default DocumentsPanel;
